// Localização: frontend/src/components/Prontuario/Profissional/RH/FeriasSection.jsx (versão 1.0.0)
// Seção de Férias do prontuário do profissional

import React, { useState, useEffect } from 'react';
import { Plus, Calendar, CheckCircle, Clock, XCircle } from 'lucide-react';
import { getFerias, createFerias } from '../../../../api/api';
import { formatDate } from '../../../../utils/formatters';
import FeriasFormModal from './FeriasFormModal';

const FeriasSection = ({ profissionalId, onRefresh }) => {
    const [ferias, setFerias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);

    useEffect(() => {
        fetchFerias();
    }, [profissionalId]);

    const fetchFerias = async () => {
        try {
            setLoading(true);
            const response = await getFerias(profissionalId);
            if (response.data.success) {
                setFerias(response.data.data || []);
            }
        } catch (error) {
            console.error('Erro ao buscar férias:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSuccess = () => {
        setIsFormOpen(false);
        fetchFerias();
        if (onRefresh) onRefresh();
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'concluida':
                return <CheckCircle className="h-5 w-5 text-green-500" />;
            case 'em_andamento':
                return <Clock className="h-5 w-5 text-blue-500" />;
            case 'cancelada':
                return <XCircle className="h-5 w-5 text-red-500" />;
            default:
                return <Calendar className="h-5 w-5 text-yellow-500" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'concluida':
                return 'bg-green-50 border-green-200';
            case 'em_andamento':
                return 'bg-blue-50 border-blue-200';
            case 'cancelada':
                return 'bg-red-50 border-red-200';
            default:
                return 'bg-yellow-50 border-yellow-200';
        }
    };

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Histórico de Férias</h3>
                <button
                    onClick={() => setIsFormOpen(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                    <Plus className="h-4 w-4" />
                    <span>Registrar Férias</span>
                </button>
            </div>

            {ferias.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                    <p>Nenhum período de férias registrado.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {ferias.map((feria) => (
                        <div
                            key={feria.id}
                            className={`border rounded-lg p-4 ${getStatusColor(feria.status)}`}
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-2">
                                        {getStatusIcon(feria.status)}
                                        <span className="font-semibold text-gray-900">
                                            {formatDate(feria.data_inicio)} - {formatDate(feria.data_fim)}
                                        </span>
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                            feria.status === 'concluida' ? 'bg-green-100 text-green-800' :
                                            feria.status === 'em_andamento' ? 'bg-blue-100 text-blue-800' :
                                            feria.status === 'cancelada' ? 'bg-red-100 text-red-800' :
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {feria.status}
                                        </span>
                                    </div>
                                    <div className="text-sm text-gray-600 space-y-1">
                                        <div>
                                            <span className="font-medium">Dias:</span> {feria.dias_totais} dias
                                        </div>
                                        <div>
                                            <span className="font-medium">Tipo:</span> {feria.tipo_ferias || 'Integral'}
                                        </div>
                                        <div>
                                            <span className="font-medium">Período Aquisitivo:</span>{' '}
                                            {formatDate(feria.periodo_aquisitivo_inicio)} - {formatDate(feria.periodo_aquisitivo_fim)}
                                        </div>
                                        {feria.abono_pecuniario && (
                                            <div>
                                                <span className="font-medium">Abono Pecuniário:</span> Sim
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <FeriasFormModal
                isOpen={isFormOpen}
                onClose={() => setIsFormOpen(false)}
                profissionalId={profissionalId}
                onSuccess={handleCreateSuccess}
            />
        </div>
    );
};

export default FeriasSection;

